//@ts-check
import {formatDateToMMDDYYYY} from '@/helpers/shadcn/utils'
import {DEFAULT_TABLE_PAGE_SIZE} from '@/utils/constants/commonConstants'
import {useCallback, useMemo, useRef, useState} from 'react'

/** @typedef {{column: string, direction: 'asc' | 'desc'}} SortEntry */
/** @typedef {{field: string, type: string, value?: string, values?: string[], operator?: string}} SearchFilter */

// Maps a column's configured `filterVariant` (as set in its *ColConfig, e.g. InventoryListColConfig)
// to the search-filter `type` sent to the API. Variants not listed here (e.g. 'text', 'dropdown')
// are UI-only and fall back to the default 'string' type.
const FILTER_VARIANT_TYPE_MAP = {
  double: 'double',
  long: 'long',
  date: 'date',
  dateRange: 'dateRange',
  string: 'string',
}

/** @param {SortEntry | null} a @param {SortEntry | null} b */
const isSameSortEntry = (a, b) => a?.column === b?.column && a?.direction === b?.direction

const defaultGetRowId = row => row.id

/**
 * Generic state hook for v2 DataTable — manages pagination, sorting, column
 * filters and global search. Exposes callbacks that can be passed directly to
 * the DataTable component props.
 *
 * @param {{ defaultPageSize?: number; debounceMs?: number; getRowId?: (row: any) => string | number; columns?: Array<{columnKey?: string, name_of_db_column?: string, filterVariant?: string}>; defaultSorting?: SortEntry | null }} [options] `columns` is the table's column config (e.g. InventoryListColConfig); each column's `filterVariant` (e.g. 'double') overrides the default 'string' search-filter type sent for that column. `defaultSorting` is shown on the table's sort indicator whenever nothing else is sorted.
 */
export const useDataTableV2 = ({
  defaultPageSize = DEFAULT_TABLE_PAGE_SIZE,
  debounceMs = 500,
  getRowId = defaultGetRowId,
  columns = [],
  defaultSorting = null,
} = {}) => {
  const columnFilterVariants = useMemo(
    () =>
      Object.fromEntries(
        columns.flatMap(col => {
          const columnKey = col.columnKey ?? col.name_of_db_column
          return columnKey && col.filterVariant ? [[columnKey, col.filterVariant]] : []
        })
      ),
    [columns]
  )
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(defaultPageSize)
  const [sorting, setSorting] = useState(/** @type {SortEntry | null} */ (null))
  const [columnFilters, setColumnFilters] = useState(/** @type {Record<string, SearchFilter>} */ ({}))
  const [selectedRows, setSelectedRows] = useState(/** @type {any[]} */ ([]))
  const [isAllRowsSelected, setIsAllRowsSelected] = useState(false)
  const [resetKey, setResetKey] = useState(0)

  const debounceTimers = useRef(/** @type {Record<string, ReturnType<typeof setTimeout>>} */ ({}))
  const tableRef = useRef(/** @type {import('@tanstack/react-table').Table<any> | null} */ (null))
  // Raw (as-typed) filter values, kept in sync so they can re-seed a freshly mounted table
  // instance's inputs - DataTable is remounted (new key) when visible columns change, which
  // resets its internal column filter/global search state even though our own `columnFilters`
  // state above (used to build the API query) is untouched.
  const rawColumnFilterValuesRef = useRef(/** @type {Record<string, unknown>} */ ({}))
  const rawGlobalFilterValueRef = useRef('')

  /** @type {(newPage: number) => void} */
  const onPageIndexChange = useCallback(
    newPage => setTimeout(() => setPageIndex(newPage - 1), 0), // DataTable sends 1-based, API expects 0-based
    []
  )

  /** @type {(newPageSize: number) => void} */
  const onRowsPerPageChange = useCallback(newPageSize => {
    setPageSize(newPageSize)
    setPageIndex(0)
  }, [])

  // Ignores repeats of the current sort (e.g. re-applying it to a remounted table) so they don't reset the page.
  /** @param {SortEntry[]} sortingState */
  const sortingHandler = useCallback(
    sortingState => {
      const nextSorting = sortingState?.[0] ?? null
      if (isSameSortEntry(sorting, nextSorting)) return
      setSorting(nextSorting)
      setPageIndex(0)
    },
    [sorting]
  )

  /**
   * @param {unknown} value
   * @param {'text' | 'dropdown' | 'date' | 'dateRange' | undefined} filterType
   * @param {string} columnKey
   */
  const onColumnFiltersChange = useCallback(
    (value, filterType, columnKey) => {
      if (value === '' || value === null || value === undefined) {
        delete rawColumnFilterValuesRef.current[columnKey]
      } else {
        rawColumnFilterValuesRef.current[columnKey] = value
      }

      clearTimeout(debounceTimers.current[columnKey])
      debounceTimers.current[columnKey] = setTimeout(() => {
        setColumnFilters(prev => {
          const next = {...prev}
          if (value === '' || value === null || value === undefined) {
            delete next[columnKey]
          } else if (filterType === 'date') {
            const formatted = formatDateToMMDDYYYY(String(value))
            if (formatted !== '-') {
              next[columnKey] = {field: columnKey, value: formatted, type: 'date'}
            } else {
              delete next[columnKey]
            }
          } else {
            const resolvedType = FILTER_VARIANT_TYPE_MAP[columnFilterVariants[columnKey]] ?? 'string'
            const str = String(value)
            // Split on commas to support multi-value filters (e.g. "123, 154" → values: ["123","154"]).
            const parts = str
              .split(',')
              .map(p => p.trim())
              .filter(Boolean)
            next[columnKey] =
              parts.length > 1
                ? {field: columnKey, values: parts, type: resolvedType}
                : {field: columnKey, value: str, type: resolvedType}
          }
          return next
        })
        setPageIndex(0)
      }, debounceMs)
    },
    [debounceMs, columnFilterVariants]
  )

  /** @param {string} value */
  const onGlobalSearchChange = useCallback(
    value => {
      rawGlobalFilterValueRef.current = value

      clearTimeout(debounceTimers.current['__global__'])
      debounceTimers.current['__global__'] = setTimeout(() => {
        setColumnFilters(prev => {
          const next = {...prev}
          if (!value) {
            delete next['__global__']
          } else {
            next['__global__'] = {field: 'globalSearch', value, type: 'string'}
          }
          return next
        })
        setPageIndex(0)
      }, debounceMs)
    },
    [debounceMs]
  )

  const clearSelection = useCallback(() => {
    setSelectedRows([])
    setIsAllRowsSelected(false)
    tableRef.current?.setRowSelection?.({})
  }, [])

  const handleResetAll = useCallback(() => {
    setTimeout(() => {
      setColumnFilters({})
      setSorting(null)
      setPageIndex(0)
      clearSelection()
      setResetKey(k => k + 1)
      rawColumnFilterValuesRef.current = {}
      rawGlobalFilterValueRef.current = ''
      tableRef.current?.resetColumnFilters?.()
    }, 0)
  }, [clearSelection])

  /**
   * Re-seeds a freshly mounted table instance's column filter/global search inputs from the
   * last raw values typed by the user, so switching visible columns (which remounts DataTable)
   * doesn't visually clear filter inputs whose underlying query filters are still applied.
   * A new DS table instance also reports empty sorting and resets the page just before this runs -
   * this callback closes over the values from before that, so it puts them back (batched, so no
   * refetch) and re-applies the sort indicator.
   * @type {(tableInstance: import('@tanstack/react-table').Table<any>) => void}
   */
  const onTableReady = useCallback(
    tableInstance => {
      tableRef.current = tableInstance

      const columnFiltersToRestore = Object.entries(rawColumnFilterValuesRef.current).map(([id, value]) => ({
        id,
        value,
      }))
      if (columnFiltersToRestore.length > 0) {
        tableInstance.setColumnFilters?.(columnFiltersToRestore)
      }

      if (rawGlobalFilterValueRef.current) {
        tableInstance.setGlobalFilter?.(rawGlobalFilterValueRef.current)
      }

      setSorting(sorting)
      setPageIndex(pageIndex)

      const sortingToSeed = sorting ?? defaultSorting
      if (sortingToSeed) {
        // The table hasn't finished its own mount render yet, so setSorting here is a no-op without this delay
        setTimeout(() => {
          tableInstance.setSorting?.([{id: sortingToSeed.column, desc: sortingToSeed.direction === 'desc'}])
        }, 0)
      }
    },
    [defaultSorting, sorting, pageIndex]
  )

  /** @param {import('@tanstack/react-table').Row<any>[]} selectedTableRows */
  const rowSelectionStateUpdater = useCallback(
    selectedTableRows => {
      // The DS table reports rows in selection order, not display order - re-sort by row
      // index so "the first selected row" always means the topmost visible row.
      const orderedRows = [...selectedTableRows].sort((a, b) => a.index - b.index)
      const reportedRows = orderedRows.map(row => row.original ?? row)
      // The DS table only remembers off-page selections made on its current instance - rows restored via
      // initialRowSelection after a remount aren't reported back, so carry over the ones still selected.
      const reportedIds = new Set(reportedRows.map(row => String(getRowId(row))))
      const rowSelectionState = tableRef.current?.getState?.().rowSelection ?? {}
      const currentData = tableRef.current?.options?.data
      const currentRowIds = new Set((currentData ?? []).map(row => String(getRowId(row))))
      const carriedOverRows = selectedRows.filter(row => {
        const rowId = String(getRowId(row))
        return !reportedIds.has(rowId) && (rowSelectionState[rowId] || (currentData && !currentRowIds.has(rowId)))
      })
      const newSelectedRows = [...reportedRows, ...carriedOverRows]
      setSelectedRows(newSelectedRows)

      const isAllPageRowsSelected = tableRef.current?.getIsAllPageRowsSelected?.() ?? false
      // The header checkbox toggles every row on the page in one call; manually checking
      // rows one by one only ever changes the count by 1, even if that lands on "all selected".
      const isBulkToggle = Math.abs(newSelectedRows.length - selectedRows.length) > 1
      setIsAllRowsSelected(isAllPageRowsSelected && isBulkToggle)
    },
    [selectedRows, getRowId]
  )

  /** @type {Set<string | number>} */
  const selectedRowIds = useMemo(() => new Set(selectedRows.map(getRowId)), [selectedRows, getRowId])

  // Seeds a remounted DataTable's checkboxes - it only reads this on mount.
  const initialRowSelection = useMemo(
    () => Object.fromEntries(selectedRows.map(row => [String(getRowId(row)), true])),
    [selectedRows, getRowId]
  )

  /** @param {any} row */
  const isRowSelected = useCallback(row => selectedRowIds.has(getRowId(row)), [selectedRowIds, getRowId])

  /** @type {SearchFilter[]} */
  const activeFilters = useMemo(() => Object.values(columnFilters), [columnFilters])

  return {
    // state
    pageIndex,
    pageSize,
    sorting,
    activeFilters,
    selectedRows,
    isAllRowsSelected,
    resetKey,
    // DataTable callbacks
    onPageIndexChange,
    onRowsPerPageChange,
    sortingHandler,
    onColumnFiltersChange,
    onGlobalSearchChange,
    handleResetAll,
    onTableReady,
    rowSelectionStateUpdater,
    // selection helpers
    isRowSelected,
    initialRowSelection,
    clearSelection,
    tableRef,
  }
}
